import { useEffect, useState } from 'react';

/**
 * ScreenReaderAnnouncements Component
 * 
 * Provides a global announcement system for screen reader users.
 * Uses ARIA live regions to announce dynamic content changes.
 * 
 * @example
 * // In your app root:
 * <ScreenReaderAnnouncements />
 * 
 * // Anywhere in your app:
 * import { announceToScreenReader } from './ScreenReaderAnnouncements';
 * announceToScreenReader('Booking confirmed successfully');
 */

// Global announcement callback
let announcementCallback: ((message: string, priority: 'polite' | 'assertive') => void) | null = null;

/**
 * Announces a message to screen readers
 * @param message - The message to announce
 * @param priority - 'polite' (default) or 'assertive'
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  if (announcementCallback) {
    announcementCallback(message, priority);
  }
}

/**
 * AnnouncementProvider Component
 * 
 * Place this component once at the root of your application.
 * It creates the necessary ARIA live regions for announcements.
 */
export function ScreenReaderAnnouncements(): React.ReactElement {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  // Register the announcement callback
  useEffect(() => {
    announcementCallback = (message: string, priority: 'polite' | 'assertive') => {
      if (priority === 'assertive') {
        setAssertiveMessage(message);
      } else {
        setPoliteMessage(message);
      }
    };

    return () => {
      announcementCallback = null;
    };
  }, []);

  // Clear messages after they're announced (with a delay)
  useEffect(() => {
    if (politeMessage) {
      const timeoutId = setTimeout(() => setPoliteMessage(''), 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [politeMessage]);

  useEffect(() => {
    if (assertiveMessage) {
      const timeoutId = setTimeout(() => setAssertiveMessage(''), 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [assertiveMessage]);

  return (
    <>
      {/* Polite announcements - won't interrupt */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {politeMessage}
      </div>
      
      {/* Assertive announcements - will interrupt */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        role="alert"
      >
        {assertiveMessage}
      </div>
    </>
  );
}

/**
 * useAnnouncer Hook
 * 
 * Hook for components that need to make announcements.
 * 
 * @example
 * const { announce } = useAnnouncer();
 * announce('Form submitted successfully');
 */
export function useAnnouncer() {
  const announce = (
    message: string,
    priority: 'polite' | 'assertive' = 'polite'
  ) => {
    announceToScreenReader(message, priority);
  };

  return { announce };
}

/**
 * AccessibleStatus Component
 * 
 * Announces status changes for async operations.
 * 
 * @example
 * <AccessibleStatus 
 *   status={loading ? 'loading' : error ? 'error' : 'success'}
 *   messages={{
 *     loading: 'Loading articles...',
 *     error: 'Failed to load articles',
 *     success: 'Articles loaded successfully'
 *   }}
 * />
 */
export interface AccessibleStatusProps {
  status: 'loading' | 'error' | 'success' | 'idle';
  messages: {
    loading?: string;
    error?: string;
    success?: string;
    idle?: string;
  };
  priority?: 'polite' | 'assertive';
}

export function AccessibleStatus({
  status,
  messages,
  priority = 'polite',
}: AccessibleStatusProps): React.ReactElement | null {
  const { announce } = useAnnouncer();

  useEffect(() => {
    const message = messages[status];
    if (message && status !== 'idle') {
      announce(message, priority);
    }
  }, [status, messages, priority, announce]);

  return null;
}

export default ScreenReaderAnnouncements;
